import { useState, useEffect } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { CREATE_SIGNNOW_DOCUMENT } from '@/shared/mutations';
import { CHECK_SIGNNOW_DOCUMENT_STATUS } from '@/shared/queries';
import { Linking } from 'react-native';
import {
  MutationResult,
  QueryResult,
  QueryCheckSignNowDocumentStatusArgs,
} from '@/shared/types/generated';

interface UseSignNowDocumentReturn {
  documentId: string;
  signingLink: string;
  contractLoading: boolean;
  contractError: string;
  isContractSigned: boolean;
  handleContractCheckboxPress: () => Promise<void>;
  handleContractLinkPress: () => Promise<void>;
}

export const useSignNowDocument = (
  isStylist?: boolean,
): UseSignNowDocumentReturn => {
  const [documentId, setDocumentId] = useState<string>('');
  const [signingLink, setSigningLink] = useState<string>('');
  const [contractLoading, setContractLoading] = useState(false);
  const [contractError, setContractError] = useState<string>('');
  const [isContractSigned, setIsContractSigned] = useState(false);

  const [createSignNowDocument] = useMutation<
    MutationResult<'createSignNowDocument'>
  >(CREATE_SIGNNOW_DOCUMENT);
  const [checkDocumentStatus] = useLazyQuery<
    QueryResult<'checkSignNowDocumentStatus'>,
    QueryCheckSignNowDocumentStatusArgs
  >(CHECK_SIGNNOW_DOCUMENT_STATUS, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (isStylist && !documentId) {
      const createDocument = async () => {
        try {
          setContractLoading(true);
          const response = await createSignNowDocument();
          if (response.data?.createSignNowDocument) {
            setDocumentId(response.data.createSignNowDocument.documentId);
            setSigningLink(response.data.createSignNowDocument.signingLink);
          }
        } catch (error) {
          setContractError(
            'Failed to create contract document. Please refresh the page.',
          );
        } finally {
          setContractLoading(false);
        }
      };
      createDocument();
    }
  }, [isStylist, documentId, createSignNowDocument]);

  const handleContractLinkPress = async () => {
    if (signingLink) {
      await Linking.openURL(signingLink);
    }
  };

  const handleContractCheckboxPress = async () => {
    if (!isContractSigned && documentId) {
      try {
        setContractLoading(true);
        setContractError('');
        const response = await checkDocumentStatus({
          variables: { documentId },
        });
        if (response.data?.checkSignNowDocumentStatus) {
          setIsContractSigned(true);
        } else {
          setContractError(
            "We couldn't find your signed contract. Make sure you complete the contract signing at the link above.",
          );
        }
      } catch (error) {
        setContractError(
          'There was an error checking your contract status. Please try again later.',
        );
      } finally {
        setContractLoading(false);
      }
    } else {
      setIsContractSigned(!isContractSigned);
    }
  };

  return {
    documentId,
    signingLink,
    contractLoading,
    contractError,
    isContractSigned,
    handleContractCheckboxPress,
    handleContractLinkPress,
  };
};
